import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useLocation, useSearch } from 'wouter';
import { ChevronLeft, AlertCircle, Copy, FileText, User, Eye, List, ArrowLeft, SkipBack, SkipForward, Users, Trash2 } from 'lucide-react';
import { ChartEditor } from '../components/ChartEditor';
import { ExportModal } from '../components/ExportModal';
import { VersionHistoryModal } from '../components/VersionHistoryModal';
import { BaseChartEditorsPanel } from '../components/BaseChartEditorsPanel';
import { api } from '../lib/api';
import { saveChartLocally, addToSyncQueue, getLocalChart } from '../lib/offlineStore';
import type { CanvasJSON, ChartMetadata } from '@shared/types';

export function ChartEditorPage() {
  const params = useParams<{ type: string; chartId: string }>();
  const chartType = params.type as 'base' | 'personal' | 'shared';
  const chartId = Number(params.chartId);
  const [, navigate] = useLocation();
  const searchString = useSearch();
  const queryClient = useQueryClient();
  const [showExport, setShowExport] = useState(false);
  const [showVersions, setShowVersions] = useState(false);
  const [showSetlistNav, setShowSetlistNav] = useState(false);
  const [showEditorsPanel, setShowEditorsPanel] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const setlistNavRef = useRef<HTMLDivElement>(null);

  const searchParams = new URLSearchParams(searchString);
  const rawSetlistId = searchParams.get('setlist');
  const rawSetlistPos = searchParams.get('pos');
  const setlistId = rawSetlistId && !isNaN(Number(rawSetlistId)) ? Number(rawSetlistId) : null;
  const setlistPos = rawSetlistPos && !isNaN(Number(rawSetlistPos)) ? Number(rawSetlistPos) : null;

  const isSharedView = chartType === 'shared';
  const inSetlistMode = setlistId !== null && setlistPos !== null;

  const { data: setlistData } = useQuery({
    queryKey: ['setlist', setlistId],
    queryFn: () => api.setlists.get(setlistId!),
    enabled: inSetlistMode,
  });

  const setlistItems = setlistData?.items || [];
  const currentSetlistItem = inSetlistMode && setlistItems.length > 0 ? setlistItems[setlistPos!] : null;

  const getSetlistChartLink = (item: any, index: number) => {
    const base = item.personalVariantId
      ? `/editor/personal/${item.personalVariantId}`
      : item.baseChartId
        ? `/editor/base/${item.baseChartId}`
        : null;
    if (!base) return null;
    return `${base}?setlist=${setlistId}&pos=${index}`;
  };

  const findAdjacentChart = (fromPos: number, direction: -1 | 1): string | null => {
    if (!inSetlistMode) return null;
    for (let i = fromPos + direction; i >= 0 && i < setlistItems.length; i += direction) {
      const link = getSetlistChartLink(setlistItems[i], i);
      if (link) return link;
    }
    return null;
  };

  const prevLink = inSetlistMode ? findAdjacentChart(setlistPos!, -1) : null;
  const nextLink = inSetlistMode ? findAdjacentChart(setlistPos!, 1) : null;

  useEffect(() => {
    if (!showSetlistNav) return;
    const handleClick = (e: MouseEvent) => {
      if (setlistNavRef.current && !setlistNavRef.current.contains(e.target as Node)) {
        setShowSetlistNav(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showSetlistNav]);

  const { data: chart, isLoading } = useQuery({
    queryKey: [isSharedView ? 'sharedChart' : chartType === 'base' ? 'baseChart' : 'personalChart', chartId],
    queryFn: async () => {
      try {
        let data;
        if (isSharedView) {
          data = await api.charts.getSharedView(chartId);
        } else if (chartType === 'base') {
          data = await api.charts.getBase(chartId);
        } else {
          data = await api.charts.getPersonalDetail(chartId);
        }
        if (!isSharedView) await saveChartLocally(`${chartType}-${chartId}`, data);
        return data;
      } catch (err) {
        if (!isSharedView) {
          const local = await getLocalChart(`${chartType}-${chartId}`);
          if (local) return local;
        }
        throw err;
      }
    },
  });

  const { data: personalVariant, isLoading: personalLoading } = useQuery({
    queryKey: ['personalVariantForBase', isSharedView ? chart?.baseChartId : chartId],
    queryFn: () => api.charts.getPersonalForBase(isSharedView ? chart.baseChartId : chartId),
    enabled: (chartType === 'base' || isSharedView) && !!chart,
  });

  const { data: songData } = useQuery({
    queryKey: ['song', chart?.songId],
    queryFn: () => api.songs.get(chart.songId),
    enabled: !!chart?.songId,
  });

  const sharedOwnerName = isSharedView ? chart?.ownerName : null;

  const deletePersonalVariant = useMutation({
    mutationFn: () => api.charts.deletePersonal(chartId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['personalVariantForBase'] });
      queryClient.invalidateQueries({ queryKey: ['personalChart', chartId] });
      queryClient.removeQueries({ queryKey: ['personalChart', chartId] });
      setShowDeleteConfirm(false);
      if (data?.baseChartId) {
        navigate(`/editor/base/${data.baseChartId}`);
      } else if (chart?.baseChartId) {
        navigate(`/editor/base/${chart.baseChartId}`);
      } else {
        navigate(backUrl);
      }
    },
    onError: (err: Error) => {
      setDeleteError(err.message || 'Deletion failed. Please try again.');
    },
  });

  const createPersonalVariant = useMutation({
    mutationFn: () => {
      if (isSharedView && chart) {
        return api.charts.createPersonal(chart.baseChartId, chartId);
      }
      return api.charts.createPersonal(chartId);
    },
    onSuccess: (variant) => {
      queryClient.invalidateQueries({ queryKey: ['personalVariantForBase'] });
      const setlistSuffix = inSetlistMode ? `?setlist=${setlistId}&pos=${setlistPos}` : '';
      navigate(`/editor/personal/${variant.id}${setlistSuffix}`);
    },
  });

  const handleSave = async (canvas: CanvasJSON, metadata: ChartMetadata | Partial<ChartMetadata>, createVersion = false) => {
    const localData = {
      ...chart,
      canvasJson: canvas,
      ...(chartType === 'base' ? { chartMetadata: metadata } : { chartMetadataOverride: metadata }),
    };
    await saveChartLocally(`${chartType}-${chartId}`, localData);

    try {
      if (chartType === 'base') {
        await api.charts.updateBase(chartId, { chartMetadata: metadata, canvasJson: canvas }, createVersion);
      } else {
        await api.charts.updatePersonal(chartId, { chartMetadataOverride: metadata, canvasJson: canvas }, createVersion);
      }
      queryClient.invalidateQueries({ queryKey: [chartType === 'base' ? 'baseChart' : 'personalChart', chartId] });

      if (chartType === 'base' && chart?.songId) {
        const protectedHeader = canvas.elements.find(
          (el: any) => el.type === 'songHeader' && el.content.protected
        );
        if (protectedHeader) {
          api.songs.update(chart.songId, {
            title: protectedHeader.content.title || undefined,
            artist: protectedHeader.content.artist || null,
            defaultKey: protectedHeader.content.key || null,
            defaultTempo: protectedHeader.content.bpm || null,
          }).then(() => {
            queryClient.invalidateQueries({ queryKey: ['song', chart.songId] });
            queryClient.invalidateQueries({ queryKey: ['songs', chart.bandId] });
          }).catch(console.error);
        }
      }
    } catch {
      await addToSyncQueue({
        type: `save-${chartType}-chart`,
        url: chartType === 'base'
          ? `/api/charts/base/${chartId}${createVersion ? '?createVersion=true' : ''}`
          : `/api/charts/personal/${chartId}${createVersion ? '?createVersion=true' : ''}`,
        method: 'PUT',
        body: chartType === 'base' ? { chartMetadata: metadata, canvasJson: canvas } : { chartMetadataOverride: metadata, canvasJson: canvas },
      });
    }
  };

  if (isLoading) {
    return <div className="h-full flex items-center justify-center bg-surface-950 text-gray-400">Loading chart...</div>;
  }

  if (!chart) {
    return <div className="h-full flex items-center justify-center bg-surface-950 text-gray-400">Chart not found</div>;
  }

  const setlistBackUrl = inSetlistMode && setlistData
    ? `/bands/${setlistData.bandId}/setlists/${setlistId}/view`
    : null;

  const backUrl = setlistBackUrl
    || (songData ? `/bands/${chart.bandId || songData.bandId}/songs/${chart.songId || songData.id}` : '/');

  const hasBaseUpdated = chartType === 'personal' && chart.baseVersionNumber != null && chart.lastSeenBaseVersion < chart.baseVersionNumber;

  const hasExistingPersonal = (chartType === 'base' || isSharedView) && personalVariant && !personalLoading;

  return (
    <div className="h-full flex flex-col">
      {inSetlistMode && setlistData && (
        <div className="bg-surface-950 border-b border-surface-700 px-3 py-1.5 flex items-center gap-2 shrink-0">
          <button
            onClick={() => navigate(setlistBackUrl!)}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {setlistData.name}
          </button>
          <div className="flex-1" />
          <button
            onClick={() => prevLink && navigate(prevLink)}
            disabled={!prevLink}
            className="p-1 hover:bg-surface-800 rounded disabled:opacity-20 transition-colors"
            title="Previous Song"
          >
            <SkipBack className="w-3.5 h-3.5 text-gray-400" />
          </button>
          <div className="relative" ref={setlistNavRef}>
            <button
              onClick={() => setShowSetlistNav(!showSetlistNav)}
              className="flex items-center gap-1 text-xs text-gray-300 hover:text-white bg-surface-800 hover:bg-surface-700 px-2 py-1 rounded transition-colors"
            >
              <List className="w-3 h-3" />
              {setlistPos! + 1} of {setlistItems.length}
            </button>
            {showSetlistNav && (
              <div className="absolute top-full right-0 mt-1 bg-surface-900 border border-surface-700 rounded-lg shadow-xl z-50 w-64 max-h-72 overflow-y-auto">
                {setlistItems.map((item: any, idx: number) => {
                  const link = getSetlistChartLink(item, idx);
                  const isCurrent = idx === setlistPos;
                  return (
                    <button
                      key={item.itemId}
                      onClick={() => {
                        if (link && !isCurrent) navigate(link);
                        setShowSetlistNav(false);
                      }}
                      disabled={!link}
                      className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-colors disabled:opacity-40 ${
                        isCurrent
                          ? 'bg-primary-600/20 text-primary-300'
                          : 'hover:bg-surface-800 text-gray-300'
                      }`}
                    >
                      <span className="font-mono text-gray-500 w-5 text-right shrink-0">{idx + 1}</span>
                      <span className="truncate flex-1">{item.songTitle}</span>
                      {item.personalVariantId && <User className="w-3 h-3 text-accent-400 shrink-0" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button
            onClick={() => nextLink && navigate(nextLink)}
            disabled={!nextLink}
            className="p-1 hover:bg-surface-800 rounded disabled:opacity-20 transition-colors"
            title="Next Song"
          >
            <SkipForward className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>
      )}

      <div className="bg-surface-900 border-b border-surface-700 px-3 py-2 flex items-center gap-2 shrink-0">
        {!inSetlistMode && (
          <button onClick={() => navigate(backUrl)} className="p-1 hover:bg-surface-800 rounded transition-colors">
            <ChevronLeft className="w-5 h-5 text-gray-400" />
          </button>
        )}
        <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
          <p className="text-sm font-medium truncate">
            {songData?.title || 'Chart'}
          </p>
          {isSharedView ? (
            <span className="shrink-0 inline-flex items-center gap-1 text-xs bg-purple-600/20 text-purple-400 px-2 py-0.5 rounded-full">
              <Eye className="w-3 h-3" />
              {sharedOwnerName ? `${sharedOwnerName}'s Version` : 'Shared View'} (Read Only)
            </span>
          ) : chartType === 'base' ? (
            <>
              <span className="shrink-0 inline-flex items-center gap-1 text-xs bg-surface-700 text-gray-300 px-2 py-0.5 rounded-full">
                <FileText className="w-3 h-3" />
                Base Chart
              </span>
              {!chart.canEdit && (
                <span className="shrink-0 inline-flex items-center gap-1 text-xs bg-surface-800 text-gray-500 px-2 py-0.5 rounded-full">
                  View Only
                </span>
              )}
            </>
          ) : (
            <span className="shrink-0 inline-flex items-center gap-1 text-xs bg-accent-600/20 text-accent-400 px-2 py-0.5 rounded-full">
              <User className="w-3 h-3" />
              My Version
            </span>
          )}
          {hasBaseUpdated && (
            <>
              <span className="shrink-0 inline-flex items-center gap-1 text-xs bg-amber-500/20 text-amber-400 px-2 py-0.5 rounded-full">
                <AlertCircle className="w-3 h-3" />
                Base updated
              </span>
              <button
                onClick={() => navigate(`/compare/personal/${chartId}`)}
                className="shrink-0 text-xs text-amber-400 hover:text-amber-300 underline transition-colors"
              >
                Compare
              </button>
              <button
                onClick={async () => {
                  await api.charts.acknowledgeUpdate(chartId);
                  queryClient.invalidateQueries({ queryKey: ['personalChart', chartId] });
                }}
                className="shrink-0 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                Dismiss
              </button>
            </>
          )}
        </div>

        {chartType === 'personal' && !isSharedView && (
          <button
            onClick={() => { setDeleteError(null); setShowDeleteConfirm(true); }}
            className="shrink-0 flex items-center gap-1.5 bg-surface-800 hover:bg-red-900/40 text-gray-400 hover:text-red-400 text-xs font-medium py-1.5 px-3 rounded-lg transition-colors border border-transparent hover:border-red-900/60"
            title="Delete My Version"
          >
            <Trash2 className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Delete My Version</span>
          </button>
        )}

        {chartType === 'base' && !isSharedView && chart.canManage && (
          <button
            onClick={() => setShowEditorsPanel(true)}
            className="shrink-0 flex items-center gap-1.5 bg-surface-700 hover:bg-surface-600 text-gray-300 text-xs font-medium py-1.5 px-3 rounded-lg transition-colors"
            title="Manage base chart editors"
          >
            <Users className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Manage Editors</span>
          </button>
        )}

        {((!isSharedView && chartType === 'base') || (isSharedView && !chart?.isOwner)) && !personalLoading && (
          hasExistingPersonal ? (
            <button
              onClick={() => {
                const suffix = inSetlistMode ? `?setlist=${setlistId}&pos=${setlistPos}` : '';
                navigate(`/editor/personal/${personalVariant.id}${suffix}`);
              }}
              className="shrink-0 flex items-center gap-1.5 bg-accent-600 hover:bg-accent-500 text-white text-xs font-medium py-1.5 px-3 rounded-lg transition-colors"
            >
              <User className="w-3.5 h-3.5" />
              Open My Version
            </button>
          ) : (
            <button
              onClick={() => createPersonalVariant.mutate()}
              disabled={createPersonalVariant.isPending}
              className="shrink-0 flex items-center gap-1.5 bg-accent-600 hover:bg-accent-500 disabled:opacity-50 text-white text-xs font-medium py-1.5 px-3 rounded-lg transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
              {createPersonalVariant.isPending ? 'Creating...' : isSharedView ? 'Create My Version from This' : 'Create My Version'}
            </button>
          )
        )}
      </div>

      <div className="flex-1 overflow-hidden">
        {chartType === 'base' && !songData ? (
          <div className="h-full flex items-center justify-center bg-surface-950 text-gray-400">Loading chart...</div>
        ) : (() => {
          const rawCanvas: CanvasJSON = chart.canvasJson || { elements: [], canvasWidth: 800, canvasHeight: 1100, zoom: 1 };
          let patchedCanvas = rawCanvas;
          if (chartType === 'base' && songData) {
            const patchedElements = rawCanvas.elements.map((el: any) => {
              if (el.type === 'songHeader' && el.content.protected) {
                return {
                  ...el,
                  content: {
                    ...el.content,
                    title: songData.title || el.content.title,
                    artist: songData.artist ?? el.content.artist,
                    key: songData.defaultKey ?? el.content.key,
                    bpm: songData.defaultTempo ?? el.content.bpm,
                  },
                };
              }
              return el;
            });
            patchedCanvas = { ...rawCanvas, elements: patchedElements };
          }
          return (
            <ChartEditor
              key={`${chartType}-${chartId}`}
              chartType={isSharedView ? 'personal' : chartType}
              chartId={chartId}
              songId={chart.songId ?? undefined}
              initialCanvas={patchedCanvas}
              initialMetadata={isSharedView ? (chart.chartMetadataOverride || chart.chartMetadata || {}) : chartType === 'base' ? (chart.chartMetadata || {}) : (chart.chartMetadataOverride || {})}
              onSave={(isSharedView || (chartType === 'base' && !chart.canEdit)) ? undefined : handleSave}
              onExportPDF={() => setShowExport(true)}
              onShowVersions={(isSharedView || (chartType === 'base' && !chart.canEdit)) ? undefined : () => setShowVersions(true)}
              readOnly={isSharedView || (chartType === 'base' && !chart.canEdit)}
            />
          );
        })()}
      </div>

      <ExportModal
        open={showExport}
        onClose={() => setShowExport(false)}
        canvasRef={null}
        songTitle={songData?.title || 'Chart'}
      />

      <VersionHistoryModal
        open={showVersions}
        onClose={() => setShowVersions(false)}
        chartType={chartType}
        chartId={chartId}
      />

      {showEditorsPanel && chartType === 'base' && chart.bandId && (
        <BaseChartEditorsPanel
          baseChartId={chartId}
          bandId={chart.bandId}
          onClose={() => setShowEditorsPanel(false)}
        />
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="bg-surface-900 border border-surface-700 rounded-xl w-full max-w-sm shadow-2xl">
            <div className="p-5 border-b border-surface-700">
              <h3 className="text-base font-semibold text-white">Delete My Version?</h3>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-300 leading-relaxed">
                This will permanently delete your personalized version of this chart. You can create a new My Version from the current Base Chart afterward.
              </p>
              {deleteError && (
                <p className="mt-3 text-xs text-red-400 bg-red-900/20 border border-red-900/40 rounded-lg px-3 py-2">
                  {deleteError}
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 pb-5">
              <button
                onClick={() => { setShowDeleteConfirm(false); setDeleteError(null); }}
                disabled={deletePersonalVariant.isPending}
                className="px-4 py-2 text-sm text-gray-300 hover:text-white bg-surface-800 hover:bg-surface-700 rounded-lg transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deletePersonalVariant.mutate()}
                disabled={deletePersonalVariant.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {deletePersonalVariant.isPending ? 'Deleting…' : 'Delete My Version'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
